import { useNavigate, useSearch } from '@tanstack/react-router';
import { LogIn } from 'lucide-react';
import { useEffect, useState } from 'react';
import { authConfig, beginSignIn, tokenStore } from '../lib/auth.ts';
import { Button } from '../components/ui/button.tsx';

/**
 * The only public page. With Cognito configured it hands the browser to the hosted UI
 * (Google only — see infra/modules/cognito); without it, it says what to configure. In a
 * dev build with the mock API, a mock session is one click away.
 */
export function SignInPage() {
  const search = useSearch({ from: '/sign-in' });
  const navigate = useNavigate();
  const cfg = authConfig();
  const [busy, setBusy] = useState(false);
  const mock = import.meta.env.DEV && import.meta.env.VITE_API_MOCK === 'true';

  useEffect(() => {
    if (tokenStore.get()) void navigate({ to: search.returnTo ?? '/', replace: true });
  }, [navigate, search.returnTo]);

  const start = async () => {
    if (!cfg) return;
    setBusy(true);
    await beginSignIn(cfg, { returnTo: search.returnTo ?? '/' });
  };
  const useMock = () => {
    tokenStore.set({ idToken: 'mock.session.token', expiresAt: Date.now() + 12 * 60 * 60_000 });
    void navigate({ to: search.returnTo ?? '/', replace: true });
  };

  return (
    <main className="relative flex min-h-dvh items-center justify-center overflow-hidden p-6">
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_50%_at_20%_10%,color-mix(in_oklab,var(--primary)_14%,transparent),transparent_70%)]"
        aria-hidden
      />
      <div className="bg-card border-border shadow-raised relative w-full max-w-sm rounded-2xl border p-8">
        <span className="bg-brand-gradient grid size-12 place-items-center rounded-2xl text-lg font-bold text-white">
          RL
        </span>
        <h1 className="mt-5 text-xl font-semibold tracking-tight">Remote Leverage CRM</h1>
        <p className="text-muted-foreground mt-1 mb-6 text-sm">
          Sign in with your work Google account.
        </p>
        {search.error && (
          <p
            className="bg-state-danger-bg text-state-danger-fg border-state-danger-border mb-4 rounded-lg border px-3 py-2 text-sm"
            role="alert"
          >
            {search.error}
          </p>
        )}
        {cfg ? (
          <Button
            className="h-10 w-full"
            onClick={() => void start()}
            disabled={busy}
            data-testid="sign-in"
          >
            <LogIn /> Continue with Google
          </Button>
        ) : (
          <p className="text-muted-foreground text-sm" data-testid="not-configured">
            Sign-in is not configured for this build: set <code>VITE_COGNITO_DOMAIN</code> and{' '}
            <code>VITE_COGNITO_CLIENT_ID</code> (the Terraform <code>hosted_ui_domain</code> and{' '}
            <code>web_client_id</code> outputs).
          </p>
        )}
        {mock && (
          <Button
            variant="outline"
            className="mt-3 w-full"
            onClick={useMock}
            data-testid="mock-sign-in"
          >
            Use the mock session (dev)
          </Button>
        )}
      </div>
    </main>
  );
}
